/**
 * AI Learning Summary
 * Shows what the AI has learned after completing all onboarding phases
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Brain, Loader2, Database, Link2, FileText, Shield, 
  Sparkles, BarChart3, HelpCircle, Download, ArrowRight,
  CheckCircle2, Server, Table, BookOpen, Lightbulb
} from 'lucide-react';
import { generateAILearningSummary } from '../../services/api';

const AILearningSummary = ({ phaseProgress, onStartQuerying }) => {
  const [state, setState] = useState({
    loading: false,
    generated: false,
    summary: null,
    error: null
  });

  // Check if all phases are complete (or mostly complete)
  const calculateOverallProgress = () => {
    if (!phaseProgress) return 0;
    const phases = Object.values(phaseProgress);
    if (phases.length === 0) return 0;
    const total = phases.reduce((sum, p) => sum + (p || 0), 0);
    return Math.round(total / 6); // 6 phases
  };

  const overallProgress = calculateOverallProgress();
  const isReady = overallProgress >= 70; // At least 70% complete

  const generateSummary = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await generateAILearningSummary();
      setState(prev => ({
        ...prev,
        loading: false,
        generated: true,
        summary: result.summary
      }));
      toast.success('AI Learning Summary generated!');
    } catch (error) {
      console.error('Failed to generate summary:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to generate summary. Please try again.'
      }));
      toast.error('Failed to generate summary');
    }
  };

  const downloadSummary = () => {
    if (!state.summary) return;
    
    const content = `
# AI Learning Summary - MyAIAnalysis
Generated: ${new Date().toLocaleDateString()}

## Business Overview
${state.summary.businessOverview || 'Not available'}

## Database Structure
- Servers: ${state.summary.stats?.servers || 0}
- Databases: ${state.summary.stats?.databases || 0}
- Tables Analyzed: ${state.summary.stats?.tablesAnalyzed || 0}
- Tables Ignored: ${state.summary.stats?.tablesIgnored || 0}
- Relationships: ${state.summary.stats?.relationships || 0}

## Key Entities
${(state.summary.keyEntities || []).map(e => `- ${e}`).join('\n')}

## Suggested Reports
${(state.summary.suggestedReports || []).map((r, i) => `${i + 1}. ${r}`).join('\n')}

## Sample Questions I Can Answer
${(state.summary.sampleQuestions || []).map(q => `- "${q}"`).join('\n')}

## Query Rules Learned
${(state.summary.queryRules || []).map(r => `- ${r}`).join('\n')}
    `.trim();

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ai-learning-summary.md';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Summary downloaded!');
  };

  if (!isReady) {
    return (
      <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
        <CardContent className="p-8 text-center">
          <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">AI Learning Summary</h3>
          <p className="text-gray-500 mb-4">
            Complete more onboarding phases to unlock the AI summary.
          </p>
          <div className="w-full max-w-xs mx-auto bg-gray-200 rounded-full h-3 mb-2">
            <div 
              className="bg-amber-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-400">{overallProgress}% complete (need 70%)</p>
        </CardContent>
      </Card>
    );
  }

  if (!state.generated) {
    return (
      <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Brain className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Ready to Generate AI Summary!</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            The AI will analyze everything you've taught it and create a comprehensive summary of 
            its understanding, including suggested reports and sample questions it can answer.
          </p>
          <Button 
            onClick={generateSummary}
            disabled={state.loading}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 px-8 py-3 text-lg"
          >
            {state.loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-5 h-5 mr-2" /> Generate AI Summary</>
            )}
          </Button>
          {state.error && (
            <p className="text-red-500 mt-4">{state.error}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const summary = state.summary;

  return (
    <Card className="bg-white border-gray-200 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-white">
            <Brain className="w-6 h-6" />
            AI Learning Summary
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              All Phases Complete
            </span>
          </CardTitle>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={downloadSummary}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={Server} label="Servers" value={summary.stats?.servers || 0} color="blue" />
          <StatCard icon={Database} label="Databases" value={summary.stats?.databases || 0} color="green" />
          <StatCard icon={Table} label="Tables" value={summary.stats?.tablesAnalyzed || 0} color="amber" />
          <StatCard icon={Link2} label="Relationships" value={summary.stats?.relationships || 0} color="purple" />
          <StatCard icon={Shield} label="Query Rules" value={summary.stats?.queryRules || 0} color="red" />
        </div>

        {/* Business Overview */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
          <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5" />
            What I've Learned About Your Business
          </h3>
          <p className="text-blue-700">{summary.businessOverview || 'Business context not provided yet.'}</p>
        </div>

        {/* Key Entities */}
        {summary.keyEntities && summary.keyEntities.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <Database className="w-5 h-5 text-green-600" />
              Key Business Entities I Understand
            </h3>
            <div className="flex flex-wrap gap-2">
              {summary.keyEntities.map((entity, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium"
                >
                  {entity}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* DBA Capabilities - What analyses can I do based on DBA queries */}
        {summary.dbaCapabilities && summary.dbaCapabilities.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Expert Analyses I Can Perform (from DBA Queries)
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {summary.dbaCapabilities.map((cap, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"
                >
                  <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-800">{cap}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Business Rules I Follow */}
        {summary.businessRules && summary.businessRules.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-purple-600" />
              Business Rules I Follow
            </h3>
            <ul className="space-y-2">
              {summary.businessRules.map((rule, idx) => (
                <li key={idx} className="flex items-start gap-2 p-2 bg-purple-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-1" />
                  <span className="text-purple-800">{rule}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggested Reports */}
        <div>
          <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Reports I Can Generate
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {(summary.suggestedReports || []).map((report, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100"
              >
                <div className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-800 text-xs font-bold">{idx + 1}</span>
                </div>
                <span className="text-amber-800">{report}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sample Questions */}
        <div>
          <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
            <HelpCircle className="w-5 h-5 text-purple-600" />
            Sample Questions I Can Answer
          </h3>
          <div className="space-y-2">
            {(summary.sampleQuestions || []).map((question, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100 hover:bg-purple-100 transition-colors cursor-pointer"
                onClick={() => onStartQuerying && onStartQuerying(question)}
              >
                <Lightbulb className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-purple-800 italic">"{question}"</span>
                <ArrowRight className="w-4 h-4 text-purple-400 ml-auto" />
              </div>
            ))}
          </div>
        </div>

        {/* Query Rules */}
        {summary.queryRules && summary.queryRules.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-red-600" />
              Query Rules I Follow
            </h3>
            <ul className="space-y-2">
              {summary.queryRules.map((rule, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="pt-4 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            Ready to put this knowledge to use?
          </p>
          <Button 
            onClick={() => onStartQuerying && onStartQuerying()}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Start Asking Questions
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    red: 'bg-red-50 text-red-600 border-red-100',
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <Icon className="w-5 h-5 mb-2" />
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-80">{label}</div>
    </div>
  );
};

export default AILearningSummary;
